/**
 * 전투 결정 로직 (일감 G1) — Phaser-free Functional Core.
 *
 * "무엇을/얼마나"의 결정(스펙 합성·기본 피해·발사 간격·골드 배수·분열 자탄 스펙)만 모은다.
 * 실행(발사체 스폰·FX·물리 충돌·트윈)은 셸(GameScene/ProjectileFactory)이 담당 — 패러다임 규칙 1·6.
 *
 * 컨트롤러 의존은 getter 주입: applySaveData가 컨트롤러 인스턴스를 통째 교체하므로
 * 값으로 받으면 reload 후 stale 참조가 된다 (gamescene-decomposition CheatActions 패턴과 동일).
 */
import { BigNumber } from "@yjsgames/bignumber";
import {
  MODULE_BRICK_BALANCE,
  MIN_FIRE_INTERVAL_MS,
  LAUNCHER,
} from "../data/balance.js";
import { findPath } from "../modules/ModulePathFinder.js";
import type { ModuleRegistry } from "../modules/ModuleRegistry.js";
import {
  effectiveRarity,
  type ModuleInstance,
} from "../models/ModuleInstance.js";
import type { ProjectileSpec } from "../models/ProjectileSpec.js";
import { createDefaultProjectileSpec } from "../models/ProjectileSpec.js";
import { applyModuleEffect } from "../modules/ModuleEffect.js";
import type { ModuleProcKind } from "../modules/ModuleEffect.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import { resolveModuleSynergies } from "./ModuleSynergyResolver.js";
import { SYNERGY_CODEX_UNLOCK_CHAPTER } from "../data/synergyUnlock.js";
import {
  composeBaseDamage,
  composeCritChance,
  composeCritMultiplier,
  composeFireIntervalMs,
  composeGoldMultiplier,
} from "./statComposition.js";
import type { ModuleGridController } from "./ModuleGridController.js";
import type { ModuleInventory } from "./ModuleInventory.js";
import type { PlayerModuleLevelController } from "./PlayerModuleLevelController.js";
import type { ModuleMasteryController } from "./ModuleMasteryController.js";
import type { SynergyLevelController } from "./SynergyLevelController.js";
import { masteryNodesOf } from "../data/moduleMasteryDefinitions.js";
import type { ProjectileSpecBuilder } from "./ProjectileSpecBuilder.js";
import { FusedModule } from "./ModuleFusion.js";
import {
  combineEnhanceMeta,
  combineEchoMeta,
  resolvePipelineEnhancements,
  computeEchoChances,
} from "./PipelineEnhancement.js";
import type { RelicController } from "./RelicController.js";
import type { StatUpgradeController } from "./StatUpgradeController.js";
import type { SpaceshipController } from "./SpaceshipController.js";
import { footprintCells } from "../data/moduleFootprints.js";
import type { GridPosition } from "../models/ModuleGrid.js";

export interface CombatDeps {
  grid: () => ModuleGridController;
  inventory: () => ModuleInventory;
  /** 모듈 레벨 — moduleId별 플레이어 자산. 인스턴스 등급과 결합해 최종 성능 = f(level, rarity) 산출. */
  playerModuleLevels: () => PlayerModuleLevelController;
  /** 모듈 숙련도 — 해금 노드 효과를 등급별 effect에 더해 적용(3번째 성장축). */
  moduleMastery: () => ModuleMasteryController;
  /** 시너지 강화 레벨 — 매칭된 조합 효과에 적용되는 영구 레벨. */
  synergyLevels: () => SynergyLevelController;
  /** 시너지 시스템 해금 기준 — 도감 시너지 잠금과 실제 전투 발동을 같은 bestChapter로 맞춘다. */
  bestChapter?: () => number;
  spaceship: () => SpaceshipController;
  moduleRegistry: () => ModuleRegistry;
  specBuilder: () => ProjectileSpecBuilder;
  relic: () => RelicController;
  statUpgrade: () => StatUpgradeController;
  rebirthUpgrade: () => StatUpgradeController;
}

/**
 * buildCombatSpec 결과 — 발사 스펙 + 이번 발사에서 발동한 시너지 id 목록(도감 발견 후보).
 * 기존 buildSpec(ProjectileSpec만 반환) 호출부는 무변경 — 발견 처리가 필요한 BattlefieldRunner만 본 결과를 쓴다.
 */
export interface CombatSpecResult {
  readonly spec: ProjectileSpec;
  readonly matchedSynergyIds: readonly string[];
}

export interface CombatFlowSpecResult extends CombatSpecResult {
  readonly flowIndex: number;
  readonly start: GridPosition;
  readonly fireIntervalMs: number;
}

export class CombatSystem {
  constructor(private readonly deps: CombatDeps) {}

  /**
   * Start→Fire 연결 경로 위 인스턴스를 경로 순서대로 적용한 최종 스펙. 경로 없으면 기본 발사체.
   * rng는 모듈 effect의 chance 발동 게이트 판정용 — fire()는 Math.random, 표시/DPS/QA 호출은 무인자
   * (= 항상 발동 가정으로 결정론 표시). 확률 가중 기대값이 아닌 "발동 시" 최댓값을 보여준다.
   */
  buildCombatSpec(
    rng?: () => number,
    onProcInstance?: (instanceId: string, kind: ModuleProcKind) => void,
  ): CombatSpecResult {
    const path = findPath(this.deps.grid());
    return this.buildCombatSpecForInstances(path.activeInstanceIds, rng, onProcInstance);
  }

  buildCombatFlowSpecs(
    rng?: () => number,
    onProcInstance?: (instanceId: string, kind: ModuleProcKind) => void,
  ): readonly CombatFlowSpecResult[] {
    return this.combatFlowInputs().map((flow) => this.buildCombatFlowSpecFromInput(flow, rng, onProcInstance));
  }

  buildCombatFlowSpec(
    flowIndex: number,
    rng?: () => number,
    onProcInstance?: (instanceId: string, kind: ModuleProcKind) => void,
  ): CombatFlowSpecResult {
    const flows = this.combatFlowInputs();
    const input = flows[flowIndex] ?? flows[0]!;
    return this.buildCombatFlowSpecFromInput(input, rng, onProcInstance);
  }

  private combatFlowInputs(): readonly {
    readonly flowIndex: number;
    readonly start: GridPosition;
    readonly instanceIds: readonly string[];
  }[] {
    const grid = this.deps.grid();
    const path = findPath(grid);
    const groupsByStart = new Map<string, (typeof path.activeFlowGroups)[number]>(
      path.activeFlowGroups.map((group) => [`${group.start.x},${group.start.y}`, group] as const),
    );
    const seenStarts = new Set<string>();
    const flows: Array<{ flowIndex: number; start: GridPosition; instanceIds: readonly string[] }> = [];
    const lanes = grid.lanes.length > 0 ? grid.lanes : [{ start: grid.start, fire: grid.fire }];
    for (const lane of lanes) {
      const startKey = `${lane.start.x},${lane.start.y}`;
      if (seenStarts.has(startKey)) continue;
      seenStarts.add(startKey);
      const group = groupsByStart.get(startKey);
      flows.push({
        flowIndex: flows.length,
        start: { ...lane.start },
        instanceIds: group?.instanceIds ?? [],
      });
    }
    return flows;
  }

  private buildCombatFlowSpecFromInput(
    flow: { readonly flowIndex: number; readonly start: GridPosition; readonly instanceIds: readonly string[] },
    rng: (() => number) | undefined,
    onProcInstance: ((instanceId: string, kind: ModuleProcKind) => void) | undefined,
  ): CombatFlowSpecResult {
    const result = this.buildCombatSpecForInstances(flow.instanceIds, rng, onProcInstance);
    return {
      ...result,
      flowIndex: flow.flowIndex,
      start: { ...flow.start },
      fireIntervalMs: this.fireIntervalMsForSpec(result.spec),
    };
  }

  private buildCombatSpecForInstances(
    instanceIds: readonly string[],
    rng: (() => number) | undefined,
    onProcInstance: ((instanceId: string, kind: ModuleProcKind) => void) | undefined,
  ): CombatSpecResult {
    const inventory = this.deps.inventory();
    const registry = this.deps.moduleRegistry();
    const playerLevels = this.deps.playerModuleLevels();
    const mastery = this.deps.moduleMastery();
    // 모듈 레벨은 인스턴스가 아니라 moduleId별 플레이어 자산 — definitionId로 조회(같은 모듈 인스턴스 공유).
    const levelOf = (definitionId: string): number => playerLevels.level(definitionId);
    // 숙련도 효과 — moduleId의 해금 노드 효과 배열(노드 정의는 TSV가 SSoT라 masteryNodesOf 주입).
    const masteryOf = (definitionId: string) =>
      mastery.masteryEffects(definitionId, masteryNodesOf(definitionId));
    const insts = instanceIds
      .map((iid) => inventory.getInstance(iid))
      .filter(
        (inst): inst is ModuleInstance =>
          !!inst && registry.has(inst.definitionId),
      );
    // 파이프라인 강화(촉매/정밀) — 각 모듈의 기본 유효등급 + "다음 모듈" 강화 메타를 모아, 경로상 바로 앞
    // 모듈의 buff(등급↑·발동확률↑)를 적용한다. spec 변형이 아닌 모듈 간 메타 효과라 순수 PipelineEnhancement가 처리.
    const slots = insts.map((inst) => {
      const def = registry.getDefinition(inst.definitionId);
      const baseRarity = effectiveRarity(inst, def);
      // 융합 모듈은 파이프라인 강화·echo를 발신하지 않음(v1 opaque) — 메타 없음(undefined effect = no-op).
      const effect = inst.fused ? undefined : def?.effects[baseRarity];
      const level = levelOf(inst.definitionId);
      const metaEffects = [effect, ...masteryOf(inst.definitionId)];
      return {
        baseRarity,
        enhance: combineEnhanceMeta(metaEffects, level),
        echo: combineEchoMeta(metaEffects, level),
      };
    });
    const enhanced = resolvePipelineEnhancements(slots);
    const modules = insts.map((inst, i) => {
      if (inst.fused) {
        // 융합 모듈 — 컴포넌트별 sub-module을 만들어 effect를 모두 적용(FusedModule). 컴포넌트는 저장 등급/레벨로 해석.
        // v1: 파이프라인 강화 등급(enhanced[i].rarity)은 융합 컴포넌트에 미반영(opaque). 미등록 컴포넌트는 방어적으로 제외.
        // slotType은 표시 base(첫 컴포넌트=definitionId) 정의를 따른다(quantity 고정 회피).
        // 융합 컴포넌트 레벨도 각 컴포넌트 definitionId의 player level (인스턴스 미저장 — 2축 분리).
        const subs = inst.fused
          .filter((c) => registry.has(c.definitionId))
          .map((c) =>
            registry.create(c.definitionId, levelOf(c.definitionId), c.rarity, masteryOf(c.definitionId)),
          );
        return new FusedModule(
          inst.definitionId,
          levelOf(inst.definitionId),
          subs,
          registry.getDefinition(inst.definitionId)?.slotType,
        );
      }
      // 합성 등급 오버라이드 + 파이프라인 강화 등급(enhanced[i].rarity)으로 등급별 effect를 선택해 적용.
      // + 해금된 숙련도 노드 효과(masteryOf)를 게이트 없이 추가 적용.
      return registry.create(
        inst.definitionId,
        levelOf(inst.definitionId),
        enhanced[i]!.rarity,
        masteryOf(inst.definitionId),
      );
    });
    // 융합 모듈은 파이프라인 강화를 수신도 하지 않음(v1 opaque 일관성) — chanceBonus 0(등급 미반영과 동일 정책).
    const chanceBonuses = enhanced.map((e, i) => (insts[i]!.fused ? 0 : e.chanceBonus));
    // echo(잔향/전조) — 이웃 효과 재발동 확률 배열. 경계·fused 이웃 처리는 computeEchoChances가 담당(단위테스트 대상).
    const { echoPrevChances, echoNextChances } = computeEchoChances(slots, insts);
    // onProc(index, kind) → 발동한 모듈의 instanceId로 변환(셀 연출용). insts와 modules는 동일 인덱스.
    const spec = this.deps.specBuilder().build(
      this.baseDamage(),
      modules,
      rng,
      onProcInstance ? (i, kind) => onProcInstance(insts[i]!.instanceId, kind) : undefined,
      chanceBonuses,
      echoPrevChances,
      echoNextChances,
    );
    // 숙련도 카운터 귀속용 — 이 발사체에 기여한 모듈 definitionId(융합 컴포넌트 포함, 중복 제거).
    // BattlefieldRunner가 발사/타격/처치 시 각 id의 카운터를 누적한다.
    const activeModuleGroups = insts.map((inst) =>
      inst.fused ? inst.fused.map((c) => c.definitionId) : [inst.definitionId],
    );
    const placedByInstanceId = new Map(
      this.deps.grid().placed().map((tile) => [tile.instanceId, tile] as const),
    );
    const activeModuleSlots = insts.map((inst, index) => {
      const tile = placedByInstanceId.get(inst.instanceId);
      const cells = tile
        ? footprintCells(tile.footprintId).map((c) => ({ x: tile.x + c.dx, y: tile.y + c.dy }))
        : [];
      return { definitionIds: activeModuleGroups[index]!, cells };
    });
    spec.sourceModuleIds = [...new Set(activeModuleGroups.flat())];
    // 유물 속도 보너스 반영 (발사체 속도 배수). projectileSpeed 스탯은 폐기 — 속도는 speed 모듈/유물로만.
    spec.speedMultiplier *= this.deps.relic().speedMultiplier();
    // 영구 강화(스탯+환생) + 런처 기본 치명타 확률을 모듈 critChance에 가산(0~1 클램프). rollCrit이 발사체별로 판정.
    // 런처(시작점)는 경로 무관 항상 존재 → 빈 그리드/모듈 0개에서도 기본 치명타가 적용된다.
    spec.critChance = composeCritChance(
      spec.critChance,
      this.deps.statUpgrade().critChanceBonus(),
      this.deps.rebirthUpgrade().critChanceBonus(),
      LAUNCHER.baseCritChance,
    );
    // 치명타 데미지 배수 = max(기본 2배 global.tsv, 모듈 critMul — legendary 3·transcendent 4) + 스탯/환생 가산.
    spec.critMultiplier = composeCritMultiplier(
      MODULE_BRICK_BALANCE.critMultiplier,
      spec.critMultiplier,
      this.deps.statUpgrade().critDamageBonus(),
      this.deps.rebirthUpgrade().critDamageBonus(),
    );
    // ── 시너지 보정 — 활성 경로 모듈 조합(spec.sourceModuleIds = 융합 컴포넌트 펼침·중복 제거)으로 발동 시너지를 판정.
    // 개별 모듈 effect와 구분된 "조합 결과 보정"이라 spec 합성 완료 후 마지막에 얹는다. 레벨은 시너지 강화 레벨을 사용한다.
    const activeModuleIds = spec.sourceModuleIds;
    const activeSlotTypes = activeModuleIds
      .map((id) => registry.slotOf(id))
      .filter((s): s is ModuleSlotType => !!s);
    const synergy =
      (this.deps.bestChapter?.() ?? SYNERGY_CODEX_UNLOCK_CHAPTER) >= SYNERGY_CODEX_UNLOCK_CHAPTER
        ? resolveModuleSynergies({
            activeModuleIds,
            activeSlotTypes,
            activeModuleGroups,
            activeModuleSlots,
          })
        : { matched: [], supports: [] };
    let combined = spec;
    for (const s of synergy.matched) combined = applyModuleEffect(combined, s.effect, this.deps.synergyLevels().level(s.id));
    return { spec: combined, matchedSynergyIds: synergy.matched.map((s) => s.id) };
  }

  /**
   * 기존 호환 wrapper — spec만 필요한 호출부(표시/DPS/QA)용. 시너지 발견 처리가 필요한
   * BattlefieldRunner.fire는 buildCombatSpec을 직접 호출해 matchedSynergyIds를 소비한다.
   */
  buildSpec(
    rng?: () => number,
    onProcInstance?: (instanceId: string, kind: ModuleProcKind) => void,
  ): ProjectileSpec {
    return this.buildCombatSpec(rng, onProcInstance).spec;
  }

  /** 기본 피해 = (기본값 + 환생 공격력 강화 + 골드 attack 강화) × 유물 공격 배수. 합성은 순수 composeBaseDamage. */
  baseDamage(): BigNumber {
    return BigNumber.fromNumber(
      composeBaseDamage(
        MODULE_BRICK_BALANCE.baseDamage,
        this.deps.rebirthUpgrade().attackBonus(),
        this.deps.statUpgrade().attackBonus(),
        this.deps.relic().attackMultiplier(),
      ),
    );
  }

  /** 현재 발사 간격(ms) = (기본 - 골드/환생 fireRate 강화 감소량) × 유물 쿨타임 배수 × 모듈 fireIntervalMultiplier, 하한 클램프. */
  private baseFireIntervalMs(): number {
    return composeFireIntervalMs(
      MODULE_BRICK_BALANCE.fireIntervalMs,
      this.deps.statUpgrade().fireRateReductionMs(),
      this.deps.rebirthUpgrade().fireRateReductionMs(),
      this.deps.relic().fireRateMultiplier(),
      MIN_FIRE_INTERVAL_MS,
    );
  }

  fireIntervalMsForSpec(spec: ProjectileSpec): number {
    return Math.max(MIN_FIRE_INTERVAL_MS, this.baseFireIntervalMs() * spec.fireIntervalMultiplier);
  }

  fireIntervalMs(): number {
    const spec = this.buildSpec();
    return this.fireIntervalMsForSpec(spec);
  }

  /** 타격 골드 배수 — 스탯/환생/유물 goldGain 합성 × 직접 타격 greed 배수(범위/연쇄 경로는 기본 1). */
  goldMultiplier(directGreedMultiplier = 1): BigNumber {
    return BigNumber.fromNumber(
      composeGoldMultiplier(
        this.deps.statUpgrade().goldMultiplier(),
        this.deps.rebirthUpgrade().goldMultiplier(),
        this.deps.relic().goldMultiplier(),
        directGreedMultiplier,
      ),
    );
  }

  /** 분열 자탄 수 — 부모 splitCount, 상한 4 (시각/성능 캡, 기존 동작 보존). */
  static splitChildCount(parent: ProjectileSpec): number {
    return Math.min(parent.splitCount, 4);
  }

  /**
   * 분열 자탄 스펙 — 1발·재분열/특수효과 제거(연쇄 분열 방지)·피해 절반. bounceCount=0이라 첫 벽/벽돌 충돌로 소멸한다.
   * 발사 각/속도/스케일은 셸(spawnSplit)이 결정한다.
   *
   * ⚠ 화이트리스트 방식(default 베이스): createDefaultProjectileSpec()의 0/안전값을 베이스로 깔고,
   * 부모에서 **명시적으로 물려받을 필드만** override한다. 신규 특수 능력 필드(pierce/chain/execute류)가
   * ProjectileSpec에 추가돼도 자동으로 기본값(0=미보유)이 적용되어 자탄에 전파되지 않는다.
   * (과거 블랙리스트 방식은 신규 전파-금지 필드를 여기에 손으로 0 초기화해야 했고, 누락 시 자탄이 부모
   *  능력을 무음 상속하는 버그가 났다 — executeThreshold/executeChance 누락 회귀 사례.)
   * 자탄이 부모 속성을 잃으면 안 되는 필드만 아래에 추가한다(누락 시 자탄 색/속도/피해 손실 = 가시적이라 즉시 발견).
   */
  static deriveSplitSpec(parent: ProjectileSpec): ProjectileSpec {
    return {
      ...createDefaultProjectileSpec(parent.baseDamage),
      // 부모에서 물려받는 필드 (자탄도 유지해야 시각/타격감 일관)
      element: parent.element,
      speedMultiplier: parent.speedMultiplier,
      critChance: parent.critChance,
      critMultiplier: parent.critMultiplier,
      goldMultiplier: parent.goldMultiplier,
      // 숙련도 카운터 귀속 — 자탄 타격/처치도 부모 기여 모듈에 누적되도록 계승.
      sourceModuleIds: parent.sourceModuleIds,
      // 자탄 고유 override
      count: 1,
      damageMultiplier: parent.damageMultiplier * 0.5,
    };
  }
}
